import { useEffect, useRef, useState } from "react";

interface NavSection {
  id: string;
  label: string;
}

// Os ids precisam casar com os anchorId passados aos SectionErrorBoundary no
// Settings.tsx. Ordem = ordem visual da pagina.
const SECTIONS: NavSection[] = [
  { id: "cfg-inicio", label: "Início" },
  { id: "cfg-equipe", label: "Equipe" },
  { id: "cfg-tipo", label: "Tipo" },
  { id: "cfg-visual", label: "Visual" },
  { id: "cfg-horarios", label: "Horários" },
  { id: "cfg-pagamentos", label: "Pagamentos" },
  { id: "cfg-entrega", label: "Entrega" },
  { id: "cfg-sugestao", label: "Sugestão" },
  { id: "cfg-promocao", label: "Promoção" },
  { id: "cfg-cupons", label: "Cupons" },
  { id: "cfg-produtos", label: "Produtos" },
  { id: "cfg-clientes", label: "Clientes" },
  { id: "cfg-pontos", label: "Pontos" },
  { id: "cfg-notificacoes", label: "Notificações" },
  { id: "cfg-sons", label: "Sons" },
  { id: "cfg-impressora", label: "Impressora" },
  { id: "cfg-pdv", label: "PDV" },
  { id: "cfg-mesa", label: "Mesa" },
  { id: "cfg-comanda", label: "Comanda" },
  { id: "cfg-mensagens", label: "Mensagens" },
  { id: "cfg-robo", label: "Robô" },
  { id: "cfg-assinatura", label: "Assinatura" },
  { id: "cfg-fim", label: "Fim" },
];

/** Disparado com o id da seção quando o lojista toca na pílula dela. */
export const EVENTO_DA_SECAO = "configuracoes-secao";

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

// Barra de navegacao sticky das Configuracoes: pills horizontais que rolam ate
// a secao e acompanham o scroll (scrollspy). So mostra botoes pras secoes que
// realmente existem no DOM (varias sao condicionais por tipo de loja/plano).
export function SettingsSectionNav() {
  const navRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [presentIds, setPresentIds] = useState<string[]>(() => SECTIONS.map((s) => s.id));
  const [activeId, setActiveId] = useState<string | null>(null);

  // Detecta as secoes presentes e re-detecta quando o Settings adiciona/remove
  // secoes (ex: troca de tipo de loja, plano, impressora suportada).
  useEffect(() => {
    const compute = () => {
      const ids = SECTIONS.filter((s) => document.getElementById(s.id)).map((s) => s.id);
      setPresentIds((prev) => (sameIds(prev, ids) ? prev : ids));
    };
    compute();
    const root = document.getElementById("settings-sections-root");
    if (!root) return;
    const mo = new MutationObserver(compute);
    mo.observe(root, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  // Scrollspy: ativa = ultima secao cujo topo passou pela linha logo abaixo da
  // barra. rAF pra nao processar a cada evento de scroll.
  useEffect(() => {
    let raf = 0;
    const recompute = () => {
      raf = 0;
      const nav = navRef.current;
      const line = (nav ? nav.getBoundingClientRect().bottom : 0) + 24;
      let current: string | null = null;
      let lastPresent: string | null = null;
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        lastPresent = s.id;
        if (current === null) current = s.id; // fallback: 1a secao quando no topo
        if (el.getBoundingClientRect().top <= line) current = s.id;
      }
      // No rodape a ultima secao (ex: "Fim") nao alcanca a linha por falta de
      // conteudo abaixo; quando chega no fim da pagina, forca ela como ativa.
      const doc = document.documentElement;
      if (lastPresent && window.innerHeight + window.scrollY >= doc.scrollHeight - 4) {
        current = lastPresent;
      }
      setActiveId((prev) => (prev === current ? prev : current));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(recompute);
    };
    recompute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [presentIds]);

  // Mantem o botao ativo visivel na barra horizontal, sem mexer no scroll
  // vertical da pagina (por isso scrollLeft manual, nao scrollIntoView).
  useEffect(() => {
    if (!activeId) return;
    const nav = navRef.current;
    const btn = btnRefs.current[activeId];
    if (!nav || !btn) return;
    const target = btn.offsetLeft - (nav.clientWidth - btn.clientWidth) / 2;
    nav.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [activeId]);

  const present = SECTIONS.filter((s) => presentIds.includes(s.id));
  if (present.length <= 1) return null;

  const goTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    setActiveId(id);
    // A seção que começa fechada abre ao chegar pela pílula (o «Gerenciar Assinatura» do plano pago), e a rolagem vem
    // depois de ela abrir: fechada no fim da página, a conta parava antes e o card ficava lá embaixo.
    window.dispatchEvent(new CustomEvent(EVENTO_DA_SECAO, { detail: id }));
    requestAnimationFrame(() => requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" })));
  };

  return (
    <div className="sticky top-14 md:top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2 bg-background/95 backdrop-blur border-b border-border/60">
      <div ref={navRef} className="flex gap-2 overflow-x-auto hide-scrollbar">
        {present.map((s) => {
          const active = s.id === activeId;
          return (
            <button
              key={s.id}
              ref={(el) => {
                btnRefs.current[s.id] = el;
              }}
              type="button"
              onClick={() => goTo(s.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                active
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/70"
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
